import { Link, useParams } from "react-router-dom";
import { ContextBar } from "../components/ContextBar";
import { ROUTES } from "../lib/routes";
import { INDUSTRY_PARTNERS, getIndustryPartner } from "../lib/industryPartners";
import { useScrollPastHero } from "../lib/useScrollPastHero";

export default function IndustryPartnerDetail() {
  const { slug = "" } = useParams<{ slug: string }>();
  const p = getIndustryPartner(slug);
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  if (!p) {
    return (
      <section className="center-hero">
        <div className="container">
          <div className="ch-eyebrow">404 · Partner</div>
          <h1 className="ch-name">Collaboration not found.</h1>
          <p style={{ color: "rgba(255,255,255,0.7)" }}>
            <Link to={ROUTES.international} style={{ color: "var(--gold-400)" }}>← Back to Industry Collaborations &amp; MoUs</Link>
          </p>
        </div>
      </section>
    );
  }

  const others = INDUSTRY_PARTNERS.filter((x) => x.slug !== p.slug).slice(0, 3);

  return (
    <article>
      <ContextBar
        visible={scrolledPast}
        tag={`${p.tag} · ${p.sector}`}
        title={p.short}
        back={{ label: "All collaborations", to: ROUTES.international }}
      />

      <section className="center-hero" ref={heroRef}>
        <div className="container">
          <nav className="event-crumbs" aria-label="Breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span aria-hidden>/</span>
            <Link to={ROUTES.international}>Industry Collaborations &amp; MoUs</Link>
            <span aria-hidden>/</span>
            <span>Partners</span>
          </nav>
          <div className="ch-eyebrow">{p.tag} · {p.sector} · {p.since}</div>
          <span className="ch-short">MOU · {p.status.toUpperCase()}</span>
          <h1 className="ch-name">{p.name}</h1>
          <p className="ch-summary">{p.desc}</p>

          <div className="ch-stats">
            <div>
              <div className="num">{p.engagements.length}</div>
              <div className="lbl">Engagement modes</div>
            </div>
            <div>
              <div className="num">{p.metrics.interns}</div>
              <div className="lbl">Interns · 5 yrs</div>
            </div>
            <div>
              <div className="num">{p.metrics.projects}</div>
              <div className="lbl">Sponsored projects</div>
            </div>
            <div>
              <div className="num">{p.faculty.length}</div>
              <div className="lbl">BIET faculty involved</div>
            </div>
          </div>
        </div>
      </section>

      <section className="detail-shell">
        <div className="container">
          <div className="detail-shell-grid">
            <div className="detail-body">
              <h2>About the collaboration</h2>
              {p.intro.map((para, i) => <p key={i}>{para}</p>)}

              <h2>Engagement modes under the MoU</h2>
              <table className="center-projects">
                <thead>
                  <tr><th>Mode</th><th>Details</th></tr>
                </thead>
                <tbody>
                  {p.engagements.map((e, i) => (
                    <tr key={i}>
                      <td className="tt">{e.kind}</td>
                      <td>{e.detail}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <h2>BIET faculty engaged with this partnership</h2>
              <div className="center-faculty">
                {p.faculty.map((f, i) => (
                  <div key={i} className="cl-cell" style={{ border: "1px solid var(--rule)" }}>
                    <div className="cl-role">{f.areas}</div>
                    <div className="cl-name">{f.name}</div>
                  </div>
                ))}
              </div>

              <h2 style={{ marginTop: "var(--space-6)" }}>Recent activity</h2>
              <ul className="alum-journey">
                {p.recent.map((r, i) => (
                  <li key={i}>
                    <div className="yr">{r.year}</div>
                    <div className="tx">{r.what}</div>
                  </li>
                ))}
              </ul>
            </div>

            <aside className="detail-meta-card is-accented">
              <div className="dmc-block">
                <div className="dmc-label">Sector</div>
                <div className="dmc-value">{p.tag} · {p.sector}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Short name</div>
                <div className="dmc-value">{p.short}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Status</div>
                <div className="dmc-value">{p.status}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Since</div>
                <div className="dmc-value">{p.since}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Departments</div>
                <div className="dmc-value">{p.depts.includes("ALL") ? "Institute-wide" : p.depts.join(" · ")}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">BIET liaison</div>
                <div className="dmc-value">{p.contact.biet}</div>
              </div>
              <div className="dmc-block">
                <div className="dmc-label">Partner liaison</div>
                <div className="dmc-value">{p.contact.partnerLiaison}</div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="event-related">
          <div className="container">
            <div className="er-head">
              <div className="kicker">More collaborations</div>
              <h2>Other industry partners</h2>
            </div>
            <div className="er-grid is-industry">
              {others.map((o) => (
                <Link key={o.slug} to={`${ROUTES.intlPartners}/${o.slug}`} className="er-card">
                  <div className="er-date">
                    <span className="er-day">{o.tag}</span>
                  </div>
                  <div className="er-body">
                    <span className="er-tag">{o.sector}</span>
                    <h3>{o.name}</h3>
                    <span className="er-venue">{o.since} · {o.status}</span>
                  </div>
                  <span className="er-arr" aria-hidden>→</span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
